import type { TranslatedString } from '@interfaces/shared';
import {
  TRADING_PROCESS_LIST,
  TradingProcessValue,
} from '@interfaces/tradingProcessList';
import { TranslatableTextField } from '@ovComponents/2-component/translatableTextField/translatableTextField';
import type {
  HouseholdModelPortfolioModel,
  HouseholdModelPortfolioWidgetOptions,
} from '@ovComponents/4-module/widgets/householdModelPortfolio/householdModelPortfolio.types';
import { WorkflowCompletionForm } from '@ovComponents/4-module/workflowCompletion/components/workflowCompletionForm';
import type { HouseholdModelPortfolioSectionSnapshot } from '@ovComponents/4-module/workflowCompletion/workflowTypes';
import { Alert, Box, Typography, useTheme } from 'hephaestus/web';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useLocalization } from '../../../../../hooks/useLocalization';
import {
  type InitializeAllocationsProblem,
  initializeAllocationsProblem,
} from '../householdModelPortfolioDraft/householdAllocations';
import { HouseholdModelPortfolioDraftPreview } from './householdModelPortfolioDraftPreview';

/**
 * Initialize has its own wording for these: the shared allocation copy sends
 * the advisor back to an earlier allocations step, and here the table they need
 * is on this screen.
 */
const INITIALIZE_ALLOCATIONS_BLOCKED_COPY: Record<
  InitializeAllocationsProblem,
  string
> = {
  EMPTY: 'empty',
  INCOMPLETE: 'incompleteHolding',
  DUPLICATE: 'duplicateHolding',
  WEIGHTS: 'doesNotAddUp',
  GROUP_HOLDINGS: 'groupsDoNotAddUp',
  GROUP_WEIGHTS: 'groupWeightsDoNotAddUp',
  DUPLICATE_TAX_RANK: 'duplicateTaxRank',
  TAX_RANK_GAPS: 'taxRankGaps',
};

/**
 * Preview field visibility toggles and label overrides for the Initialize step.
 * Keep in sync with workflow-service's initializeHouseholdModelPortfolioOptions
 * (src/useCases/subStepOptions/initializeHouseholdModelPortfolio.ts) under the
 * same key names.
 */
export type InitializeHouseholdModelPortfolioOptions = Pick<
  HouseholdModelPortfolioWidgetOptions,
  | 'showModelStrategy'
  | 'modelStrategyLabel'
  | 'showModelName'
  | 'modelNameLabel'
  | 'showModelFamily'
  | 'modelFamilyLabel'
  | 'showRiskProfile'
  | 'showRiskReturnLevel'
  | 'showSuggestible'
  | 'showZeroPercentCash'
  | 'showCashEquivalentSecurity'
  | 'showLowThresholdScore'
  | 'showMinimumScore'
  | 'showMaximumScore'
  | 'showForecastedRateOfReturn'
  | 'showForecastedStandardDeviation'
  | 'showFundamentalDetailsLink'
  | 'showDescription'
  | 'descriptionLabel'
  | 'showInvestmentObjective'
  | 'investmentObjectiveLabel'
  | 'showLiquidity'
  | 'liquidityLabel'
  | 'showDisclaimer'
  | 'disclaimerLabel'
  | 'showTargetAllocations'
  | 'targetAllocationsLabel'
> & {
  title?: TranslatedString;
  subtitle?: TranslatedString;
  /** Defaults to true when unset, see workflow-service Initialize step options. */
  allowAllocationEditing?: boolean;
  /** Defaults to true when unset; only applies when allocation editing is on. */
  allowCsvImport?: boolean;
  /** Reserved for workflow builder; summary visibility follows the committed value. */
  showTradingProcess?: boolean;
};

export type InitializeHouseholdModelPortfolioSummary = {
  parentModelName?: TranslatedString;
  tradingProcess?: string;
};

export type InitializeHouseholdModelPortfolioVisualProps = {
  options: InitializeHouseholdModelPortfolioOptions;
  loading?: boolean;
  onNext: () => void | Promise<void>;
  draftModel?: HouseholdModelPortfolioModel;
  summary?: InitializeHouseholdModelPortfolioSummary;
  /** The parent model as selected, to tell the advisor's edits apart from it. */
  baseModelSnapshot?: HouseholdModelPortfolioSectionSnapshot;
  /** The name for the household's copy. Empty leaves the naming to the backend. */
  modelName?: TranslatedString;
  onModelNameChange?: (value: TranslatedString) => void;
};

const SummaryRow = ({
  label,
  value,
  dataTestId,
}: {
  label: string;
  value: string;
  dataTestId?: string;
}) => (
  <Box
    data-testid={dataTestId}
    sx={{
      display: 'flex',
      justifyContent: 'space-between',
      gap: 2,
      py: 0.5,
    }}
  >
    <Typography variant='bodyMedium' sx={{ fontWeight: 500 }}>
      {label}
    </Typography>
    <Typography variant='bodyMedium' sx={{ fontWeight: 600 }}>
      {value}
    </Typography>
  </Box>
);

export const InitializeHouseholdModelPortfolioVisual = ({
  options,
  loading = false,
  onNext,
  draftModel,
  summary,
  baseModelSnapshot,
  modelName,
  onModelNameChange,
}: InitializeHouseholdModelPortfolioVisualProps) => {
  const { translateBackendString } = useLocalization();
  const { t } = useTranslation('workflowCompletions');
  const theme = useTheme();

  const showableTradingProcess =
    summary?.tradingProcess &&
    summary.tradingProcess !== TradingProcessValue.NoAutomation
      ? summary.tradingProcess
      : undefined;

  // Only known once select-parent has written real selections into context
  const hasSelection = Boolean(
    summary?.parentModelName || showableTradingProcess,
  );
  const allocationEditingEnabled = options.allowAllocationEditing !== false;
  const allocationsProblem = useMemo(
    () =>
      allocationEditingEnabled && draftModel
        ? initializeAllocationsProblem(draftModel, baseModelSnapshot)
        : undefined,
    [allocationEditingEnabled, draftModel, baseModelSnapshot],
  );

  return (
    <WorkflowCompletionForm
      onSubmit={onNext}
      submitButtonProps={{
        label: t('householdInitializeShells.initializeButton'),
        // No draft means select-parent hasn't seeded a model yet — there's
        // nothing to review or commit, so block advancing (the info alert below
        // explains why) rather than letting the user skip the review.
        disabled: loading || !draftModel || !!allocationsProblem,
        dataTestId: 'initialize-household-model-portfolio-continue-button',
      }}
    >
      <Typography variant='displayLarge' sx={{ mt: 1 }}>
        {translateBackendString(options?.title)}
      </Typography>
      <Typography variant='bodyLarge' sx={{ mb: 3 }}>
        <ReactMarkdown linkTarget='_blank' remarkPlugins={[remarkGfm]}>
          {translateBackendString(options?.subtitle) || ''}
        </ReactMarkdown>
      </Typography>
      {hasSelection && (
        <Box
          data-testid='household-initialize-summary'
          sx={{
            p: 2,
            mb: 3,
            background: theme.color.surfaceContainerVariant,
            borderRadius: theme.borderRadius.md,
          }}
        >
          <Typography variant='headingSmall' sx={{ mb: 1 }}>
            {t('householdInitializeShells.summary.title')}
          </Typography>
          {summary?.parentModelName && (
            <SummaryRow
              dataTestId='household-initialize-summary-parent-model'
              label={t('householdInitializeShells.summary.parentModel')}
              value={translateBackendString(summary.parentModelName)}
            />
          )}
          {showableTradingProcess && (
            <SummaryRow
              dataTestId='household-initialize-summary-trading-process'
              label={t('householdInitializeShells.summary.tradingProcess')}
              value={
                TRADING_PROCESS_LIST.find(
                  (option) => option.value === showableTradingProcess,
                )?.name ?? showableTradingProcess
              }
            />
          )}
        </Box>
      )}
      {draftModel && onModelNameChange && (
        <TranslatableTextField
          testId='household-initialize-model-name'
          label={t('householdInitializeShells.modelName.label')}
          helpText={t('householdInitializeShells.modelName.helpText')}
          value={modelName}
          onChange={onModelNameChange}
          disabled={loading}
          fullWidth
          sx={{ mb: 3 }}
        />
      )}
      {allocationsProblem ? (
        <Alert
          severity='error'
          sx={{ mb: 3 }}
          data-testid='household-initialize-allocations-problem'
        >
          {t(
            `householdModelPortfolioAllocations.initializeBlocked.${INITIALIZE_ALLOCATIONS_BLOCKED_COPY[allocationsProblem]}`,
          )}
        </Alert>
      ) : null}
      {draftModel ? (
        <HouseholdModelPortfolioDraftPreview
          model={draftModel}
          options={options}
        />
      ) : (
        <Alert severity='info'>
          {t('householdInitializeShells.noDraftSelected')}
        </Alert>
      )}
    </WorkflowCompletionForm>
  );
};
